import type { JsonObject, JsonValue } from "./canonicalLineage";
import { apiClient } from "./client";
import {
  compareInstantsAtMicrosecondPrecision,
  normalizeInstantToUtcMicroseconds,
} from "../lib/temporalInstant";

export const JOURNAL_ENTRY_WRITE_PATH =
  "/thread/v1/provenance/human/journal-entries" as const;
export const JOURNAL_ENTRY_WRITE_CONTRACT_VERSION =
  "thread.journal-entry-write.v1" as const;
export const JOURNAL_ENTRY_WRITE_RESOURCE_LIMITS_VERSION =
  "thread.journal-entry-write-resource-limits.v1" as const;
export const JOURNAL_ENTRY_WRITE_SCHEMA_MIGRATION = "20260811120000" as const;

export const JOURNAL_ENTRY_WRITE_LIMITS = {
  maximumRequestBodyBytes: 2_097_152,
  maximumJsonDepth: 32,
  maximumJsonNodes: 4_096,
  maximumJsonCanonicalBytes: 262_144,
  maximumAggregateJsonNodes: 32_768,
  maximumAggregateJsonCanonicalBytes: 2_097_152,
} as const;

export interface JournalLogicalArtifactDraft {
  logical_id: string;
  kind: "journal_entry_content";
  name: string;
  metadata: JsonObject;
}

export interface JournalContentIdentity {
  algorithm: "sha256";
  digest: string;
  canonicalizer: "rfc8785";
  canonicalizer_version: "1";
  byte_length: number;
  media_type: string;
  source_content_hash: string | null;
}

export interface JournalArtifactVersionDraft {
  artifact: JournalLogicalArtifactDraft;
  content_identity: JournalContentIdentity;
  structured_content: JsonValue;
  verified_upload_id: null;
  metadata: JsonObject;
  schema_id: string | null;
  unit_context_id: string | null;
  coordinate_frame_id: string | null;
}

export interface AppendJournalEntryRequest {
  project_id: string;
  subject_artifact_version_id: string;
  decision_id?: string | null;
  entry_type: string;
  effective_at: string;
  sensitivity_tier_id?: string | null;
  idempotency_key: string;
  content: JournalArtifactVersionDraft;
}

export interface NormalizedAppendJournalEntryRequest {
  project_id: string;
  subject_artifact_version_id: string;
  decision_id: string | null;
  entry_type: string;
  effective_at: string;
  sensitivity_tier_id: string | null;
  idempotency_key: string;
  content: JournalArtifactVersionDraft;
}

export interface JournalEntryWriteResult {
  contract_version: typeof JOURNAL_ENTRY_WRITE_CONTRACT_VERSION;
  id: string;
  project_id: string;
  subject_artifact_version_id: string;
  content_artifact_version_id: string;
  decision_id: string | null;
  entry_type: string;
  author_actor_id: string;
  effective_at: string;
  recorded_at: string;
  publication_operation_id: string;
  audit_event_id: string;
  domain_event_id: string;
  replayed: boolean;
}

export interface JournalEntryWriteEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: JournalEntryWriteResult;
  page: null;
  provenance: {
    source: "thread_core";
    projection_version: typeof JOURNAL_ENTRY_WRITE_CONTRACT_VERSION;
    schema_migration: typeof JOURNAL_ENTRY_WRITE_SCHEMA_MIGRATION;
  };
  warnings: [];
}

export interface JournalEntryWriteCapability {
  available: boolean;
  contract: typeof JOURNAL_ENTRY_WRITE_CONTRACT_VERSION;
  resourceLimitsVersion: typeof JOURNAL_ENTRY_WRITE_RESOURCE_LIMITS_VERSION;
  limits: typeof JOURNAL_ENTRY_WRITE_LIMITS;
}

type UnknownRecord = Record<string, unknown>;

const DOCUMENT_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const REQUEST_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ENTRY_TYPE_PATTERN = /^[a-z][a-z0-9_.-]{0,99}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const SOURCE_CONTENT_HASH_PATTERN = /^[a-z0-9_-]+:[0-9a-f]+$/;
const CAPABILITY_PROJECTION_VERSION = "thread.provenance.v2" as const;
const CAPABILITY_MIN_SCHEMA_MIGRATION = "20260810210000" as const;
const AWARE_TIMESTAMP_PATTERN =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;
const AWARE_TIMESTAMP_PARTS =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|(([+-])(\d{2}):(\d{2})))$/;
const FIXED_UTC_TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const PYTHON_STRIP_WHITESPACE = new Set([
  ...Array.from({ length: 5 }, (_, index) => String.fromCharCode(0x09 + index)),
  ...Array.from({ length: 5 }, (_, index) => String.fromCharCode(0x1c + index)),
  "\u0085", "\u00a0", "\u1680", "\u2000", "\u2001", "\u2002", "\u2003",
  "\u2004", "\u2005", "\u2006", "\u2007", "\u2008", "\u2009", "\u200a",
  "\u2028", "\u2029", "\u202f", "\u205f", "\u3000",
]);

export class JournalEntryWriteValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "JournalEntryWriteValidationError";
    this.path = path;
  }
}

export class JournalEntryWriteApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: string | null;
  readonly retryable: boolean;
  readonly requestId: string;

  constructor({
    status,
    code,
    message,
    field,
    retryable,
    requestId,
  }: {
    status: number;
    code: string;
    message: string;
    field: string | null;
    retryable: boolean;
    requestId: string;
  }) {
    super(message);
    this.name = "JournalEntryWriteApiError";
    this.status = status;
    this.code = code;
    this.field = field;
    this.retryable = retryable;
    this.requestId = requestId;
  }
}

function fail(path: string, expectation: string): never {
  throw new JournalEntryWriteValidationError(path, expectation);
}

function record(value: unknown, path: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    fail(path, "expected an object");
  }
  return value as UnknownRecord;
}

function exactRecord(
  value: unknown,
  path: string,
  required: readonly string[],
  optional: readonly string[] = [],
): UnknownRecord {
  const item = record(value, path);
  for (const key of required) {
    if (!Object.prototype.hasOwnProperty.call(item, key)) {
      fail(`${path}.${key}`, "missing required field");
    }
  }
  const allowed = new Set([...required, ...optional]);
  for (const key of Object.keys(item)) {
    if (!allowed.has(key)) fail(`${path}.${key}`, "unknown field on a known contract object");
  }
  return item;
}

function text(value: unknown, path: string, min = 1, max = Number.MAX_SAFE_INTEGER): string {
  if (typeof value !== "string") fail(path, "expected a string");
  const length = [...value].length;
  if (length < min || length > max) fail(path, `expected between ${min} and ${max} characters`);
  requireDatabaseSafeText(value, path);
  return value;
}

function requestUuid(value: unknown, path: string): string {
  const parsed = text(value, path);
  if (!REQUEST_UUID_PATTERN.test(parsed)) fail(path, "expected a canonically hyphenated UUID");
  return parsed.toLowerCase();
}

function documentUuid(value: unknown, path: string): string {
  const parsed = text(value, path);
  if (!DOCUMENT_UUID_PATTERN.test(parsed)) fail(path, "expected a lowercase canonical UUID");
  return parsed;
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function literal<T extends string>(value: unknown, path: string, expected: T): T {
  if (value !== expected) fail(path, `expected ${expected}`);
  return expected;
}

function nullableUuid(value: unknown, path: string, request: boolean): string | null {
  return value === null ? null : request ? requestUuid(value, path) : documentUuid(value, path);
}

function requireDatabaseSafeText(value: string, path: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code === 0) fail(path, "contains a database-unsafe Unicode code point");
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) fail(path, "contains an invalid Unicode scalar");
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      fail(path, "contains an invalid Unicode scalar");
    }
  }
}

function validAwareTimestampComponents(value: string): boolean {
  const match = AWARE_TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[11]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[12]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const normalizedMillis = Date.parse(value);
  const normalizedUtcYear = Number.isFinite(normalizedMillis)
    ? new Date(normalizedMillis).getUTCFullYear()
    : 0;
  return year >= 1
    && month >= 1 && month <= 12
    && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && ((year !== 1 && year !== 9999) || match[8] === "Z")
    && normalizedUtcYear >= 1 && normalizedUtcYear <= 9999;
}

function awareTimestamp(value: unknown, path: string): string {
  const parsed = text(value, path);
  if (!AWARE_TIMESTAMP_PATTERN.test(parsed) || !validAwareTimestampComponents(parsed)) {
    fail(path, "expected a valid RFC 3339 timestamp with a timezone offset");
  }
  try {
    return normalizeInstantToUtcMicroseconds(parsed);
  } catch {
    fail(path, "expected a valid RFC 3339 timestamp with a timezone offset");
  }
}

function fixedUtcTimestamp(value: unknown, path: string): string {
  const parsed = text(value, path);
  if (!FIXED_UTC_TIMESTAMP_PATTERN.test(parsed) || !validAwareTimestampComponents(parsed)) {
    fail(path, "expected a UTC timestamp with exactly six fractional digits");
  }
  return parsed;
}

interface JsonUsage {
  nodes: number;
  canonicalBytes: number;
}

/** RFC 8785 serialization inside Thread's finite/safe cross-language number domain. */
export function canonicalizeJournalJson(value: JsonValue, path = "content.structured_content"): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) {
      fail(path, "contains an unsafe or non-finite number");
    }
    return JSON.stringify(value);
  }
  if (typeof value === "string") {
    requireDatabaseSafeText(value, path);
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry, index) => canonicalizeJournalJson(entry, `${path}[${index}]`)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => {
    requireDatabaseSafeText(key, `${path}.<key>`);
    return `${JSON.stringify(key)}:${canonicalizeJournalJson(value[key]!, `${path}.${key}`)}`;
  }).join(",")}}`;
}

function jsonUsage(value: unknown, path: string): JsonUsage {
  let nodes = 0;
  const active = new Set<object>();
  const stack: Array<{ value: unknown; depth: number; leaving?: boolean; path: string }> = [
    { value, depth: 1, path },
  ];
  while (stack.length) {
    const frame = stack.pop()!;
    if (frame.leaving) {
      active.delete(frame.value as object);
      continue;
    }
    nodes += 1;
    if (nodes > JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonNodes) {
      fail(path, "exceeds the JSON node limit");
    }
    if (frame.depth > JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonDepth) {
      fail(path, "exceeds the JSON depth limit");
    }
    const entry = frame.value;
    if (entry === null || typeof entry === "boolean") continue;
    if (typeof entry === "string") {
      requireDatabaseSafeText(entry, frame.path);
      continue;
    }
    if (typeof entry === "number") {
      if (!Number.isFinite(entry) || (Number.isInteger(entry) && !Number.isSafeInteger(entry))) {
        fail(frame.path, "contains an unsafe or non-finite number");
      }
      continue;
    }
    if (typeof entry !== "object") fail(frame.path, "contains a non-JSON value");
    if (active.has(entry)) fail(frame.path, "contains cyclic JSON");
    const prototype = Object.getPrototypeOf(entry);
    if (!Array.isArray(entry) && prototype !== Object.prototype && prototype !== null) {
      fail(frame.path, "contains a non-JSON object");
    }
    active.add(entry);
    stack.push({ ...frame, leaving: true });
    if (Array.isArray(entry)) {
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ value: entry[index], depth: frame.depth + 1, path: `${frame.path}[${index}]` });
      }
    } else {
      const item = entry as UnknownRecord;
      const keys = Object.keys(item);
      nodes += keys.length;
      if (nodes > JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonNodes) fail(path, "exceeds the JSON node limit");
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        requireDatabaseSafeText(key, `${frame.path}.<key>`);
        stack.push({ value: item[key], depth: frame.depth + 1, path: `${frame.path}.${key}` });
      }
    }
  }
  const canonical = canonicalizeJournalJson(value as JsonValue, path);
  const canonicalBytes = new TextEncoder().encode(canonical).byteLength;
  if (canonicalBytes > JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonCanonicalBytes) {
    fail(path, "exceeds the JSON canonical byte limit");
  }
  return { nodes, canonicalBytes };
}

function jsonObject(value: unknown, path: string): JsonObject {
  const item = record(value, path);
  jsonUsage(item, path);
  return item as JsonObject;
}

function parseContent(value: unknown, path: string, request: boolean): JournalArtifactVersionDraft {
  const item = exactRecord(value, path, [
    "artifact", "content_identity", "structured_content", "verified_upload_id", "metadata",
    "schema_id", "unit_context_id", "coordinate_frame_id",
  ]);
  const artifact = exactRecord(item.artifact, `${path}.artifact`, ["logical_id", "kind", "name", "metadata"]);
  const identity = exactRecord(item.content_identity, `${path}.content_identity`, [
    "algorithm", "digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type",
    "source_content_hash",
  ]);
  const logicalId = requestUuid(artifact.logical_id, `${path}.artifact.logical_id`);
  literal(artifact.kind, `${path}.artifact.kind`, "journal_entry_content");
  const name = text(artifact.name, `${path}.artifact.name`, 1, 300);
  const artifactMetadata = jsonObject(artifact.metadata, `${path}.artifact.metadata`);
  literal(identity.algorithm, `${path}.content_identity.algorithm`, "sha256");
  const digest = text(identity.digest, `${path}.content_identity.digest`);
  if (!SHA256_PATTERN.test(digest)) fail(`${path}.content_identity.digest`, "expected 64 lowercase hexadecimal characters");
  literal(identity.canonicalizer, `${path}.content_identity.canonicalizer`, "rfc8785");
  literal(identity.canonicalizer_version, `${path}.content_identity.canonicalizer_version`, "1");
  if (!Number.isSafeInteger(identity.byte_length) || (identity.byte_length as number) < 0) {
    fail(`${path}.content_identity.byte_length`, "expected a non-negative safe integer");
  }
  const mediaType = text(identity.media_type, `${path}.content_identity.media_type`, 1, 255);
  if (PYTHON_STRIP_WHITESPACE.has(mediaType[0]!)
    || PYTHON_STRIP_WHITESPACE.has(mediaType[mediaType.length - 1]!)) {
    fail(`${path}.content_identity.media_type`, "must not contain surrounding whitespace");
  }
  if ([...mediaType].some((character) => {
    const codePoint = character.codePointAt(0)!;
    return codePoint < 0x20 || codePoint === 0x7f;
  })) {
    fail(`${path}.content_identity.media_type`, "must be printable text");
  }
  const sourceContentHash = identity.source_content_hash === null
    ? null
    : text(identity.source_content_hash, `${path}.content_identity.source_content_hash`);
  if (sourceContentHash !== null && !SOURCE_CONTENT_HASH_PATTERN.test(sourceContentHash)) {
    fail(`${path}.content_identity.source_content_hash`, "expected a typed lowercase content hash");
  }
  if (item.structured_content === null || item.structured_content === undefined) {
    fail(`${path}.structured_content`, "the browser authoring contract requires non-null structured JSON content");
  }
  if (item.verified_upload_id !== null) {
    fail(`${path}.verified_upload_id`, "the browser authoring contract does not mix structured content with an upload");
  }
  const structuredUsage = jsonUsage(item.structured_content, `${path}.structured_content`);
  const metadata = jsonObject(item.metadata, `${path}.metadata`);
  const metadataUsage = jsonUsage(metadata, `${path}.metadata`);
  const artifactMetadataUsage = jsonUsage(artifactMetadata, `${path}.artifact.metadata`);
  if (structuredUsage.nodes + metadataUsage.nodes + artifactMetadataUsage.nodes
    > JOURNAL_ENTRY_WRITE_LIMITS.maximumAggregateJsonNodes) {
    fail(path, "exceeds the aggregate JSON node limit");
  }
  if (structuredUsage.canonicalBytes + metadataUsage.canonicalBytes + artifactMetadataUsage.canonicalBytes
    > JOURNAL_ENTRY_WRITE_LIMITS.maximumAggregateJsonCanonicalBytes) {
    fail(path, "exceeds the aggregate JSON canonical byte limit");
  }
  const nullable = (entry: unknown, entryPath: string) => nullableUuid(entry, entryPath, request);
  return {
    artifact: {
      logical_id: logicalId,
      kind: "journal_entry_content",
      name,
      metadata: artifactMetadata,
    },
    content_identity: {
      algorithm: "sha256",
      digest,
      canonicalizer: "rfc8785",
      canonicalizer_version: "1",
      byte_length: identity.byte_length as number,
      media_type: mediaType,
      source_content_hash: sourceContentHash,
    },
    structured_content: item.structured_content as JsonValue,
    verified_upload_id: null,
    metadata,
    schema_id: nullable(item.schema_id, `${path}.schema_id`),
    unit_context_id: nullable(item.unit_context_id, `${path}.unit_context_id`),
    coordinate_frame_id: nullable(item.coordinate_frame_id, `${path}.coordinate_frame_id`),
  };
}

function receiptKey(value: unknown, path: string): string {
  const key = text(value, path, 1, 200);
  const first = key[0]!;
  const last = key[key.length - 1]!;
  if (PYTHON_STRIP_WHITESPACE.has(first) || PYTHON_STRIP_WHITESPACE.has(last)) {
    fail(path, "must not have boundary whitespace");
  }
  return key;
}

export function normalizeAppendJournalEntryRequest(
  value: AppendJournalEntryRequest,
): NormalizedAppendJournalEntryRequest {
  const item = exactRecord(value, "request", [
    "project_id", "subject_artifact_version_id", "entry_type", "effective_at", "idempotency_key", "content",
  ], ["decision_id", "sensitivity_tier_id"]);
  const entryType = text(item.entry_type, "request.entry_type", 1, 100);
  if (!ENTRY_TYPE_PATTERN.test(entryType)) {
    fail("request.entry_type", "expected a canonical lowercase journal entry type");
  }
  const normalized: NormalizedAppendJournalEntryRequest = {
    project_id: requestUuid(item.project_id, "request.project_id"),
    subject_artifact_version_id: requestUuid(
      item.subject_artifact_version_id,
      "request.subject_artifact_version_id",
    ),
    decision_id: nullableUuid(item.decision_id ?? null, "request.decision_id", true),
    entry_type: entryType,
    effective_at: awareTimestamp(item.effective_at, "request.effective_at"),
    sensitivity_tier_id: nullableUuid(
      item.sensitivity_tier_id ?? null,
      "request.sensitivity_tier_id",
      true,
    ),
    idempotency_key: receiptKey(item.idempotency_key, "request.idempotency_key"),
    content: parseContent(item.content, "request.content", true),
  };
  const rawBytes = serializedJournalEntryWriteBytes(normalized);
  if (rawBytes > JOURNAL_ENTRY_WRITE_LIMITS.maximumRequestBodyBytes) {
    fail("request", `exceeds the ${JOURNAL_ENTRY_WRITE_LIMITS.maximumRequestBodyBytes}-byte HTTP body limit`);
  }
  return normalized;
}

export function serializedJournalEntryWriteBytes(value: NormalizedAppendJournalEntryRequest): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) fail("crypto", "WebCrypto SHA-256 is required for journal publication");
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function materializeStructuredJournalContent({
  logicalId,
  name,
  structuredContent,
}: {
  logicalId: string;
  name: string;
  structuredContent: JsonValue;
}): Promise<JournalArtifactVersionDraft> {
  jsonUsage(structuredContent, "content.structured_content");
  const canonical = canonicalizeJournalJson(structuredContent);
  const bytes = new TextEncoder().encode(canonical);
  if (bytes.byteLength > JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonCanonicalBytes) {
    fail("content.structured_content", "exceeds the JSON canonical byte limit");
  }
  return parseContent({
    artifact: {
      logical_id: logicalId,
      kind: "journal_entry_content",
      name,
      metadata: {},
    },
    content_identity: {
      algorithm: "sha256",
      digest: await sha256Hex(bytes),
      canonicalizer: "rfc8785",
      canonicalizer_version: "1",
      byte_length: bytes.byteLength,
      media_type: "application/json",
      source_content_hash: null,
    },
    structured_content: structuredContent,
    verified_upload_id: null,
    metadata: {},
    schema_id: null,
    unit_context_id: null,
    coordinate_frame_id: null,
  }, "content", true);
}

function parseWriteResult(value: unknown, path: string): JournalEntryWriteResult {
  const item = exactRecord(value, path, [
    "contract_version", "id", "project_id", "subject_artifact_version_id",
    "content_artifact_version_id", "decision_id", "entry_type", "author_actor_id",
    "effective_at", "recorded_at", "publication_operation_id", "audit_event_id",
    "domain_event_id", "replayed",
  ]);
  literal(item.contract_version, `${path}.contract_version`, JOURNAL_ENTRY_WRITE_CONTRACT_VERSION);
  const entryType = text(item.entry_type, `${path}.entry_type`, 1, 100);
  if (!ENTRY_TYPE_PATTERN.test(entryType)) fail(`${path}.entry_type`, "expected a canonical lowercase journal entry type");
  const effectiveAt = fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  if (compareInstantsAtMicrosecondPrecision(effectiveAt, recordedAt) > 0) {
    fail(path, "effective_at must not follow recorded_at");
  }
  return {
    contract_version: JOURNAL_ENTRY_WRITE_CONTRACT_VERSION,
    id: documentUuid(item.id, `${path}.id`),
    project_id: documentUuid(item.project_id, `${path}.project_id`),
    subject_artifact_version_id: documentUuid(
      item.subject_artifact_version_id,
      `${path}.subject_artifact_version_id`,
    ),
    content_artifact_version_id: documentUuid(
      item.content_artifact_version_id,
      `${path}.content_artifact_version_id`,
    ),
    decision_id: nullableUuid(item.decision_id, `${path}.decision_id`, false),
    entry_type: entryType,
    author_actor_id: documentUuid(item.author_actor_id, `${path}.author_actor_id`),
    effective_at: effectiveAt,
    recorded_at: recordedAt,
    publication_operation_id: documentUuid(
      item.publication_operation_id,
      `${path}.publication_operation_id`,
    ),
    audit_event_id: documentUuid(item.audit_event_id, `${path}.audit_event_id`),
    domain_event_id: documentUuid(item.domain_event_id, `${path}.domain_event_id`),
    replayed: boolean(item.replayed, `${path}.replayed`),
  };
}

export function parseJournalEntryWriteEnvelope(
  value: unknown,
  request?: NormalizedAppendJournalEntryRequest,
): JournalEntryWriteEnvelope {
  const envelope = exactRecord(value, "$", [
    "schema_version", "request_id", "data", "page", "provenance", "warnings",
  ]);
  literal(envelope.schema_version, "$.schema_version", "thread.api.v1");
  const requestId = documentUuid(envelope.request_id, "$.request_id");
  if (envelope.page !== null) fail("$.page", "expected null for a command response");
  if (!Array.isArray(envelope.warnings) || envelope.warnings.length !== 0) {
    fail("$.warnings", "expected an empty warning list for a command response");
  }
  const provenance = exactRecord(envelope.provenance, "$.provenance", [
    "source", "projection_version", "schema_migration",
  ]);
  literal(provenance.source, "$.provenance.source", "thread_core");
  literal(
    provenance.projection_version,
    "$.provenance.projection_version",
    JOURNAL_ENTRY_WRITE_CONTRACT_VERSION,
  );
  literal(
    provenance.schema_migration,
    "$.provenance.schema_migration",
    JOURNAL_ENTRY_WRITE_SCHEMA_MIGRATION,
  );
  const data = parseWriteResult(envelope.data, "$.data");
  if (request) {
    if (data.project_id !== request.project_id) fail("$.data.project_id", "must equal request.project_id");
    if (data.subject_artifact_version_id !== request.subject_artifact_version_id) {
      fail("$.data.subject_artifact_version_id", "must equal request.subject_artifact_version_id");
    }
    if (data.decision_id !== request.decision_id) fail("$.data.decision_id", "must equal request.decision_id");
    if (data.entry_type !== request.entry_type) fail("$.data.entry_type", "must equal request.entry_type");
    if (compareInstantsAtMicrosecondPrecision(data.effective_at, request.effective_at) !== 0) {
      fail("$.data.effective_at", "must equal the request effective_at instant");
    }
  }
  return {
    schema_version: "thread.api.v1",
    request_id: requestId,
    data,
    page: null,
    provenance: {
      source: "thread_core",
      projection_version: JOURNAL_ENTRY_WRITE_CONTRACT_VERSION,
      schema_migration: JOURNAL_ENTRY_WRITE_SCHEMA_MIGRATION,
    },
    warnings: [],
  };
}

function parseStructuredApiError(error: unknown): JournalEntryWriteApiError | null {
  if (!(error instanceof Error)) return null;
  const match = /^API (\d{3}): ([\s\S]*)$/.exec(error.message);
  if (!match) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(match[2]!);
  } catch {
    return null;
  }
  try {
    const envelope = exactRecord(payload, "error", ["schema_version", "error"]);
    literal(envelope.schema_version, "error.schema_version", "thread.api.v1");
    const body = exactRecord(envelope.error, "error.error", [
      "code", "message", "field", "retryable", "request_id",
    ]);
    return new JournalEntryWriteApiError({
      status: Number(match[1]),
      code: text(body.code, "error.error.code"),
      message: text(body.message, "error.error.message"),
      field: body.field === null ? null : text(body.field, "error.error.field"),
      retryable: boolean(body.retryable, "error.error.retryable"),
      requestId: documentUuid(body.request_id, "error.error.request_id"),
    });
  } catch {
    return null;
  }
}

export function journalEntryWriteErrorMessage(error: unknown): string {
  if (error instanceof JournalEntryWriteValidationError) return error.message;
  const apiError = error instanceof JournalEntryWriteApiError ? error : parseStructuredApiError(error);
  if (!apiError) return error instanceof Error ? error.message : "Journal publication failed.";
  if (apiError.code === "THREAD_IDEMPOTENCY_CONFLICT") {
    return "This publication key is already bound to different content. Start a new publication attempt.";
  }
  if (apiError.code === "THREAD_NOT_FOUND") {
    return "The selected subject or decision is no longer available. It may be missing, restricted, or outside this project.";
  }
  if (apiError.code === "THREAD_VALIDATION_FAILED" && apiError.field === "effective_at") {
    return "The effective time is not valid for publication. It must not be in the future.";
  }
  if (apiError.code === "THREAD_VALIDATION_FAILED" && apiError.field === "body") {
    return "The publication body is malformed or exceeds the 2,097,152-byte request limit.";
  }
  if (apiError.code === "THREAD_REQUEST_INVALID") {
    return "The journal publication does not match the strict write contract.";
  }
  return apiError.message;
}

async function verifyStructuredContentIdentity(request: NormalizedAppendJournalEntryRequest): Promise<void> {
  const canonical = canonicalizeJournalJson(request.content.structured_content, "request.content.structured_content");
  const bytes = new TextEncoder().encode(canonical);
  if (bytes.byteLength !== request.content.content_identity.byte_length) {
    fail("request.content.content_identity.byte_length", "must equal the RFC 8785 structured-content byte length");
  }
  const digest = await sha256Hex(bytes);
  if (digest !== request.content.content_identity.digest) {
    fail("request.content.content_identity.digest", "must identify the exact RFC 8785 structured content");
  }
}

export async function appendJournalEntry(
  request: AppendJournalEntryRequest,
  options: { signal?: AbortSignal } = {},
): Promise<JournalEntryWriteEnvelope> {
  const normalized = normalizeAppendJournalEntryRequest(request);
  await verifyStructuredContentIdentity(normalized);
  try {
    const response = await apiClient<unknown>(JOURNAL_ENTRY_WRITE_PATH, {
      method: "POST",
      body: normalized,
      signal: options.signal,
    });
    return parseJournalEntryWriteEnvelope(response, normalized);
  } catch (error) {
    const mapped = parseStructuredApiError(error);
    throw mapped ?? error;
  }
}

export function parseJournalEntryWriteCapability(value: unknown): JournalEntryWriteCapability {
  const envelope = exactRecord(value, "$", [
    "schema_version", "request_id", "data", "page", "provenance", "warnings",
  ]);
  literal(envelope.schema_version, "$.schema_version", "thread.api.v1");
  documentUuid(envelope.request_id, "$.request_id");
  if (envelope.page !== null) fail("$.page", "expected null for capabilities");
  if (!Array.isArray(envelope.warnings) || envelope.warnings.length !== 0) {
    fail("$.warnings", "expected an empty capability warning list");
  }
  const provenance = exactRecord(envelope.provenance, "$.provenance", [
    "source", "projection_version", "schema_migration",
  ]);
  literal(provenance.source, "$.provenance.source", "thread_core");
  literal(
    provenance.projection_version,
    "$.provenance.projection_version",
    CAPABILITY_PROJECTION_VERSION,
  );
  const schemaMigration = text(provenance.schema_migration, "$.provenance.schema_migration");
  if (!/^\d{14}$/.test(schemaMigration) || schemaMigration < CAPABILITY_MIN_SCHEMA_MIGRATION) {
    fail(
      "$.provenance.schema_migration",
      `expected a migration marker at or after ${CAPABILITY_MIN_SCHEMA_MIGRATION}`,
    );
  }
  const data = record(envelope.data, "$.data");
  literal(data.authority, "$.data.authority", "thread_core_v3");
  const limits = record(data.limits, "$.data.limits");
  const exactLimit = (field: string, expected: number) => {
    if (limits[field] !== expected) fail(`$.data.limits.${field}`, `expected ${expected}`);
  };
  exactLimit(
    "maximum_journal_entry_write_request_body_bytes",
    JOURNAL_ENTRY_WRITE_LIMITS.maximumRequestBodyBytes,
  );
  exactLimit("maximum_journal_entry_write_json_depth", JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonDepth);
  exactLimit("maximum_journal_entry_write_json_nodes", JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonNodes);
  exactLimit(
    "maximum_journal_entry_write_json_canonical_bytes",
    JOURNAL_ENTRY_WRITE_LIMITS.maximumJsonCanonicalBytes,
  );
  exactLimit(
    "maximum_journal_entry_write_aggregate_json_nodes",
    JOURNAL_ENTRY_WRITE_LIMITS.maximumAggregateJsonNodes,
  );
  exactLimit(
    "maximum_journal_entry_write_aggregate_json_canonical_bytes",
    JOURNAL_ENTRY_WRITE_LIMITS.maximumAggregateJsonCanonicalBytes,
  );
  literal(
    data.human_provenance_journal_write_contract,
    "$.data.human_provenance_journal_write_contract",
    JOURNAL_ENTRY_WRITE_CONTRACT_VERSION,
  );
  literal(
    data.human_provenance_journal_write_resource_limits,
    "$.data.human_provenance_journal_write_resource_limits",
    JOURNAL_ENTRY_WRITE_RESOURCE_LIMITS_VERSION,
  );
  return {
    available: boolean(
      data.human_provenance_journal_write,
      "$.data.human_provenance_journal_write",
    ),
    contract: JOURNAL_ENTRY_WRITE_CONTRACT_VERSION,
    resourceLimitsVersion: JOURNAL_ENTRY_WRITE_RESOURCE_LIMITS_VERSION,
    limits: JOURNAL_ENTRY_WRITE_LIMITS,
  };
}

export async function fetchJournalEntryWriteCapability(
  projectId: string,
  options: { signal?: AbortSignal } = {},
): Promise<JournalEntryWriteCapability> {
  const normalizedProjectId = requestUuid(projectId, "project_id");
  const response = await apiClient<unknown>(
    `/thread/v1/capabilities?project_id=${encodeURIComponent(normalizedProjectId)}`,
    { signal: options.signal },
  );
  return parseJournalEntryWriteCapability(response);
}
